import React, { useState } from 'react';
import { useModalClose } from '../../../utils/useModalClose';
import { InputWrapper } from '../../common/input-wrapper';
import { Button } from 'rsuite';
import { useForm } from 'react-hook-form';
import { AlertService } from '../../../services/alert.service';
import { trpc } from '../../../api/trpc';
import { useGlobalMutate } from '../../../api/useGlobalMutate';

type Props = {
  close: () => void;
  reject: () => void;
  orderId: string;
  currentDescriptionRu?: string;
  currentDescriptionEn?: string;
  currentDescriptionCn?: string;
};

type FormValues = {
  descriptionRu: string;
  descriptionEn: string;
  descriptionCn: string;
};

export const RetailDeliveryDescriptionModal: React.FC<Props> = ({
  close,
  reject,
  orderId,
  currentDescriptionRu,
  currentDescriptionEn,
  currentDescriptionCn,
}) => {
  const { mutate: mutateDescription } = useGlobalMutate();
  const modalRef = useModalClose(reject);

  const { register, handleSubmit, setValue, watch, formState } =
    useForm<FormValues>({
      defaultValues: {
        descriptionRu: currentDescriptionRu ?? '',
        descriptionEn: currentDescriptionEn ?? '',
        descriptionCn: currentDescriptionCn ?? '',
      },
    });

  const { errors, isValid, isSubmitting } = formState;

  const descriptionRu = watch('descriptionRu');

  const [isTranslating, setIsTranslating] = useState<boolean>(false);

  const handleTranslate = async () => {
    if (!descriptionRu.trim()) return;
    setIsTranslating(true);

    try {
      const result = await trpc.translation.translate.query({
        text: descriptionRu,
      });

      setValue('descriptionEn', result.en ?? '', { shouldValidate: true });
      setValue('descriptionCn', result.zh ?? '', { shouldValidate: true });

      AlertService.success('Перевод выполнен');
    } catch (error: any) {
      AlertService.error(error?.message || 'Ошибка при переводе');
    } finally {
      setIsTranslating(false);
    }
  };

  const onSubmit = handleSubmit(async (data: FormValues) => {
    if (!orderId) return;

    try {
      await trpc.retailDelivery.requestAdmin.description.mutate({
        orderId: orderId,
        descriptionRu: data.descriptionRu,
        descriptionEn: data.descriptionEn,
        descriptionZh: data.descriptionCn,
      });
      mutateDescription();
      AlertService.success('Описание сохранено');
      close();
    } catch (error: any) {
      AlertService.error(error?.message || 'Ошибка при сохранении');
    }
  });

  return (
    <div className="modal-dialog" ref={modalRef}>
      <div className="modal-content">
        <div className="modal-body d-flex flex-column gap-3">
          <div className="d-flex justify-content-between align-items-center">
            <h3>
              {descriptionRu
                ? 'Изменить описание товара'
                : 'Добавить описание товара'}
            </h3>
            <div className="btn p-0" onClick={reject}>
              <i className="bi bi-x fs-36 color-gray-600" />
            </div>
          </div>

          <InputWrapper>
            <div className="d-flex gap-2 align-items-center">
              <input
                className="form-control"
                placeholder="Русский язык"
                maxLength={128}
                {...register('descriptionRu', { required: true })}
              />
              <Button className="btn btn-success align-items-center d-flex">
                {isTranslating ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <div onClick={handleTranslate}>Перевести</div>
                )}
              </Button>
            </div>
            {errors.descriptionRu && (
              <span className="text-danger">Обязательное поле</span>
            )}
            <span className="small text-muted">
              Короткое описание до 128 символов
            </span>
          </InputWrapper>

          <InputWrapper>
            <input
              className="form-control"
              placeholder="Английский язык"
              maxLength={128}
              {...register('descriptionEn', { required: true })}
            />
            {errors.descriptionEn && (
              <span className="text-danger">Обязательное поле</span>
            )}
          </InputWrapper>

          <InputWrapper>
            <input
              className="form-control"
              placeholder="Китайский язык"
              maxLength={128}
              {...register('descriptionCn', { required: true })}
            />
            {errors.descriptionCn && (
              <span className="text-danger">Обязательное поле</span>
            )}
          </InputWrapper>
        </div>

        <div className="modal-footer justify-content-end">
          <Button
            className="btn btn-success w-100"
            disabled={!isValid || isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? 'Сохраняем...' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </div>
  );
};
